import { createContext, useContext, useState, useEffect, useMemo, useCallback, startTransition } from 'react';
import PropTypes from 'prop-types';
import { fIsPatientUser, fIsUserLoggedIn } from 'lib/appFx';
import { fetchCurrentUserProfile } from 'services/niuHealthApi';

//---------------------------------------------------------

const initialValues = {
  // Raw data
  patientDetails: null,
  loading: false,
  error: null,
  
  // Computed subscription status
  hasActiveSubscription: false,
  personalDetailsCompleted: false,
  hasApprovedIdentification: false,
  
  // Utility functions
  refetchProfile: () => Promise.resolve(),
  isEligibleForSubscriptionCheck: false,
};

export const SubscriptionContext = createContext(initialValues);

/**
 * Custom hook to consume SubscriptionContext
 * @returns {Object} Subscription context value
 */
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

/**
 * SubscriptionProvider - Provides subscription status for home page components
 * 
 * Only makes API calls for logged-in patient users, following the same pattern
 * as the existing popup system but without the page restrictions.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export default function SubscriptionProvider({ children }) {
  const [patientDetails, setPatientDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check if user qualifies for subscription checks (synchronous checks only)
  const isEligibleForSubscriptionCheck = useMemo(() => {
    try {
      const isLoggedIn = fIsUserLoggedIn();
      const isPatient = fIsPatientUser();
      return isLoggedIn && isPatient;
    } catch (err) {
      console.error('Error checking user eligibility for subscription checks:', err);
      return false;
    }
  }, []);

  // Compute subscription status from patient details
  const subscriptionStatus = useMemo(() => {
    if (!patientDetails?.status || !patientDetails.data) {
      return {
        hasActiveSubscription: false,
        personalDetailsCompleted: false,
        hasApprovedIdentification: false,
      };
    }

    return {
      hasActiveSubscription: !!patientDetails.data.has_active_subscription,
      personalDetailsCompleted: !!patientDetails.data.personal_details_completed,
      hasApprovedIdentification: !!patientDetails.data.has_approved_identification_document,
    };
  }, [patientDetails]);

  // Fetch user profile function
  const fetchProfile = useCallback(async () => {
    if (!isEligibleForSubscriptionCheck) {
      setPatientDetails(null);
      setLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;
    
    // Use startTransition to avoid React Suspense issues (following existing pattern)
    const timeoutId = setTimeout(() => {
      if (!isMounted) return;
      
      startTransition(async () => {
        setLoading(true);
        setError(null);

        try {
          const profileResponse = await fetchCurrentUserProfile();
          
          if (isMounted) {
            if (profileResponse.status) {
              setPatientDetails(profileResponse);
              setError(null);
            } else {
              console.error('Failed to fetch profile for subscription check:', profileResponse.error);
              // For subscription checks, we gracefully degrade rather than showing errors
              setError(null);
              setPatientDetails(null);
            }
          }
        } catch (err) {
          if (isMounted) {
            console.error('Error fetching user profile for subscription check:', err);
            // For subscription checks, we gracefully degrade rather than showing errors
            setError(null);
            setPatientDetails(null);
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      });
    }, 100); // Small delay to avoid suspense issues (following existing pattern)

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [isEligibleForSubscriptionCheck]);

  // Refetch profile function for external use
  const refetchProfile = useCallback(async () => {
    if (isEligibleForSubscriptionCheck) {
      await fetchProfile();
    }
  }, [fetchProfile, isEligibleForSubscriptionCheck]);

  // Fetch profile on mount and when eligibility changes
  useEffect(() => {
    let cleanup;
    
    const initiateFetch = async () => {
      cleanup = await fetchProfile();
    };
    
    initiateFetch();
    
    return () => {
      if (cleanup && typeof cleanup === 'function') {
        cleanup();
      }
    };
  }, [fetchProfile]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    // Raw data
    patientDetails,
    loading,
    error,
    
    // Computed subscription status
    ...subscriptionStatus,
    
    // Utility functions
    refetchProfile,
    isEligibleForSubscriptionCheck,
  }), [
    patientDetails,
    loading,
    error,
    subscriptionStatus,
    refetchProfile,
    isEligibleForSubscriptionCheck,
  ]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

SubscriptionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};